import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RecintoItemResultsViewModel, RecintoPIAEdListViewModel } from '../../../recintos/recinto.types';
import { CodigoConfeccionItemResultsViewModel } from 'src/app/components/_mantenimiento/registros/codigosconfeccion/codigoconfeccion.types';
import { DialogMode, EnumDescripcionTcTipoSolicitudCamPlural, EnumDescripcionTcTipoSolicitudCamSingular, EnumIdsTcTipoSolicitudCam } from '../../../types/enum';
import { FilterSearchingBaseViewModel } from 'src/app/components/core/_infrastructure/types/common';
import { catchError, of, tap } from 'rxjs';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { CodigoConfeccionService } from 'src/app/components/_mantenimiento/registros/codigosconfeccion/codigoconfeccion.service';
import {
    EmpresaDeshidratadoraItemResultsViewModel,
    EmpresasListViewModel,
} from 'src/app/components/_mantenimiento/general/empresasdeshidratadoras/empresadeshidratadora.types';
import { EmpresaDeshidratadoraService } from 'src/app/components/_mantenimiento/general/empresasdeshidratadoras/empresadeshidratadora.service';
import { CultivoService } from '../../../cultivos/cultivo.service';

@Component({
    selector: 'app-solicitud-cam-envio-errores',
    templateUrl: './solicitud-cam-envio-errores.component.html',
    styleUrls: ['./solicitud-cam-envio-errores.component.scss'],
})
export class SolicitudCamEnvioErroresComponent extends ComponentBase implements OnChanges {
    @Input() IdTipoSolicitudCam: number;
    @Input() recintosPIA: number[];

    recintoListMode = DialogMode.Embedded;
    isCollapsedCabecera = false;
    visibilitySpinner = false;

    // Variables relacionadas con la lista de almacenes
    almacenes: CodigoConfeccionItemResultsViewModel;

    // Variables relacionadas con la lista de empresas deshidratación
    empresasDeshidratacion: EmpresaDeshidratadoraItemResultsViewModel;

    // Variables relacionadas con la lista de recintos PI
    recintosPI: RecintoItemResultsViewModel;
    lastFilterRecintos: FilterSearchingBaseViewModel;

    // Recinto Detail view params
    recintoPIADetailVisible = false;
    recintoPIAIdSelected: number | null;

    constructor(
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        protected notificacionService: NotificationService,
        private cultivoService: CultivoService,
        private codigoConfeccionService: CodigoConfeccionService,
        private empresaDeshidratadoraService: EmpresaDeshidratadoraService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.recintosPIA?.currentValue) {
            this.getRecintosPIA(changes?.recintosPIA?.currentValue);
        }
    }

    private getRecintosPIA(ids: number[]): void {
        switch (this.IdTipoSolicitudCam) {
            case EnumIdsTcTipoSolicitudCam.ID_CONFECCION:
                this.getAlmacenesDetail(ids);
                break;
            case EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION:
                this.getEmpresasDetail(ids);
                break;
            default:
                this.getCultivosDetail(ids);
                break;
        }
    }

    private getCultivosDetail(ids: number[]): void {
        if (!ids || ids.length === 0) {
            return;
        }
        this.visibilitySpinner = true;
        const payload = new RecintoPIAEdListViewModel();
        payload.Ids = ids;
        this.cultivoService
            .getCultivosByList(payload)
            .pipe(
                tap(response => {
                    this.visibilitySpinner = false;
                    this.recintosPI = response;
                    this.lastFilterRecintos = new FilterSearchingBaseViewModel();
                    this.lastFilterRecintos.PageNumber = this.recintosPI?.PageCount;
                }),
                catchError(response => {
                    this.catchServerError(response);
                    this.visibilitySpinner = false;
                    return of(null);
                }),
            )
            .subscribe();
    }

    getAlmacenesDetail(ids: number[]): void {
        if (!ids || ids.length === 0) {
            return;
        }
        this.visibilitySpinner = true;
        const payload = new RecintoPIAEdListViewModel();
        payload.Ids = ids;
        this.codigoConfeccionService
            .getAlmacenesByList(payload)
            .pipe(
                tap(response => {
                    this.visibilitySpinner = false;
                    this.almacenes = response;
                    this.lastFilterRecintos = new FilterSearchingBaseViewModel();
                    this.lastFilterRecintos.PageNumber = this.almacenes?.PageCount;
                }),
                catchError(response => {
                    this.catchServerError(response);
                    this.visibilitySpinner = false;
                    return of(null);
                }),
            )
            .subscribe();
    }

    getEmpresasDetail(ids: number[]): void {
        if (!ids || ids.length === 0) {
            return;
        }
        this.visibilitySpinner = true;
        const payload = new EmpresasListViewModel();
        payload.IdsEmpresa = ids;
        this.empresaDeshidratadoraService
            .getEmpresasDeshidratadorasByList(payload)
            .pipe(
                tap(response => {
                    this.visibilitySpinner = false;
                    this.empresasDeshidratacion = response;
                    this.lastFilterRecintos = new FilterSearchingBaseViewModel();
                    this.lastFilterRecintos.PageNumber = this.empresasDeshidratacion?.PageCount;
                }),
                catchError(response => {
                    this.catchServerError(response);
                    this.visibilitySpinner = false;
                    return of(null);
                }),
            )
            .subscribe();
    }

    onViewHandler(idRecintoPIA: number | null): void {
        this.recintoPIAIdSelected = idRecintoPIA;
        this.recintoPIADetailVisible = true;
    }

    /**
     * Se captura el evento que indica que la ventana de diálogo que muestra el detalle del recinto
     * se ha cerrado.
     * @param $event any
     */
    onRecintoDialogClosed($event: any): void {
        this.recintoPIADetailVisible = false;
    }

    get esTipoAlmacenes(): boolean {
        return this.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_CONFECCION;
    }

    get esTipoEmpresasDeshidratacion(): boolean {
        // El periodo tendrá que estar configurado para admitir emp. desh.
        return this.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION;
    }

    get esTipoRecintosPI(): boolean {
        return this.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_INVERNADERO || this.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_PARCELA;
    }

    get titleByTipoSolicitud(): any {
        if (!this.IdTipoSolicitudCam)
            return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };

        switch (this.IdTipoSolicitudCam) {
            case EnumIdsTcTipoSolicitudCam.ID_CONFECCION:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.CONFECCION, singular: EnumDescripcionTcTipoSolicitudCamSingular.CONFECCION };
            case EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.EMPRESA_DESHIDRATACION,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.EMPRESA_DESHIDRATACION,
                };
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.PARCELA,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.PARCELA,
                };
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.INVERNADERO,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.INVERNADERO,
                };
            default:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };
        }
    }
}
